import { useEffect, useRef, type ElementType, type ReactNode } from "react";
import {
  prefersReducedMotion,
  useRevealVisible,
} from "@project/user/components/use-reveal";

export type RevealVariant =
  | "up"
  | "zoom"
  | "left"
  | "right"
  | "fade"
  | "morph"
  | "wipe-left"
  | "wipe-right"
  | "wipe-up"
  | "wipe-down";

const VARIANT_CLASS: Record<RevealVariant, string> = {
  up: "reveal",
  zoom: "reveal-zoom",
  left: "reveal-left",
  right: "reveal-right",
  fade: "reveal-fade",
  morph: "reveal-morph",
  "wipe-left": "reveal-wipe-left",
  "wipe-right": "reveal-wipe-right",
  "wipe-up": "reveal-wipe-up",
  "wipe-down": "reveal-wipe-down",
};

export function Reveal({
  children,
  variant = "up",
  delay = 0,
  className = "",
  as: As = "div",
  once = true,
}: {
  children: ReactNode;
  variant?: RevealVariant;
  delay?: number;
  className?: string;
  as?: ElementType;
  once?: boolean;
}) {
  const ref = useRef<HTMLElement | null>(null);
  const visible = useRevealVisible(ref, once);

  return (
    <As
      ref={ref as never}
      data-visible={visible ? "true" : "false"}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
      className={`${VARIANT_CLASS[variant]} ${className}`}
    >
      {children}
    </As>
  );
}

export function CountUp({
  to,
  duration = 1600,
  suffix = "",
  className = "",
}: {
  to: number;
  duration?: number;
  suffix?: string;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }
    const format = (n: number) => `${n.toLocaleString("en-IN")}${suffix}`;
    // No observer or reduced motion: show the real value straight away.
    if (typeof IntersectionObserver === "undefined" || prefersReducedMotion()) {
      el.textContent = format(to);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || started.current) {
          return;
        }
        started.current = true;
        const start = performance.now();
        const tick = (now: number) => {
          const p = Math.min(1, (now - start) / duration);
          const eased = 1 - Math.pow(1 - p, 3);
          if (ref.current) {
            ref.current.textContent = format(Math.round(to * eased));
          }
          if (p < 1) {
            requestAnimationFrame(tick);
          }
        };
        requestAnimationFrame(tick);
        io.disconnect();
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [to, duration, suffix]);

  return (
    <span ref={ref} className={className}>
      0{suffix}
    </span>
  );
}
